import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { getToken } from 'firebase/app-check';
import { appCheck, auth } from '../firebase';
import storageService from '../storageService';

export interface ApiConfig {
  baseURL: string;
  timeout?: number;
}

class HttpClient {
  private client: AxiosInstance;

  constructor(config: ApiConfig) {
    this.client = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 3600000, // 1 hour (matches Cloud Function 3600s max timeout)
    });
  }

  getInstance(): AxiosInstance {
    return this.client;
  }

  async setupAuthInterceptor(): Promise<void> {
    this.client.interceptors.request.use(
      async (config) => {
        // Check for encryption key availability before making the request
        const whitelist = [
          '/login',
          '/getAccountDetails',
          '/logout',
          '/validateInvitationToken',
          '/createUser',
          '/getActivityData',
          '/getStats',
          '/getPlatformSettings',
          '/getClients',
          '/getUsers',
          '/updateAccountDetails',
          '/migrateLibraryToKms',
          '/migrateFirestoreData',
          // Python backend services that handle their own KMS decryption
          '/extract-compliance-matrix-service',
          '/run-nli-red-team-service',
          '/evaluate-supplier-claims-service',
          '/generate-clarifications-service',
          '/extract-clarification-questions-service',
          '/process-new-tender-zip-service',
          '/export-report-docx-service',
          '/export-report-presentation-service',
          '/synthesize-moderation-report-service',
          '/review-response-service',
          '/draft-response-gen',
          '/draft-response-prep',
        ];
        const isWhitelisted = config.url && whitelist.some((path) => config.url?.includes(path));

        if (
          !isWhitelisted &&
          storageService.isEncryptionEnabled() &&
          !storageService.getEncryptionKey()
        ) {
          return Promise.reject(new Error('Encryption key missing. Please log in again.'));
        }

        // Attach App Check Token
        if (appCheck) {
          try {
            const appCheckTokenResponse = await getToken(appCheck, false);
            if (appCheckTokenResponse.token) {
              if (config.headers && typeof config.headers.set === 'function') {
                config.headers.set('X-Firebase-AppCheck', appCheckTokenResponse.token);
              } else if (config.headers) {
                config.headers['X-Firebase-AppCheck'] = appCheckTokenResponse.token;
              }
            }
          } catch (_error) {
            // Silently fail if App Check token generation fails.
            // The backend will enforce the rules and reject the request if the token is required.
          }
        }

        if (auth.currentUser) {
          try {
            const token = await auth.currentUser.getIdToken();
            if (config.headers && typeof config.headers.set === 'function') {
              config.headers.set('Authorization', `Bearer ${token}`);
            } else if (config.headers) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          } catch (error) {
            return Promise.reject(error);
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, { ...config, data });
    return response.data;
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }
}

// Create and configure the HTTP client instance
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

if (!API_BASE_URL) {
  // API_BASE_URL is required but will use empty string if not set
}

export const httpClient = new HttpClient({
  baseURL: API_BASE_URL,
});

// Setup auth interceptor
httpClient.setupAuthInterceptor();

export default httpClient;
